import { ReactElement, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { BasicFade } from '@components/animators/fade';
import { Slide } from '@components/animators/slide';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { NumberFieldExampleCard } from '@components/form-builder/fields/number';
import { useConfirmModal } from '@components/generic-modals/confirm-modal';
import { IconProps } from '@components/icons';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconReplace } from '@components/icons/replace';
import { IconStatusIndicator } from '@components/icons/status-indicator';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { CloseButton, Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { Color } from '@styles/theme';
import { XOR } from '@utils/types';

import { useCompanyTravellerAttributeTemplate } from 'api/attributes/hooks';
import {
  AttributeFieldType,
  AttributeTemplateData,
  TravellerAttributeTemplateData,
} from 'api/attributes/requests';
import { visibilityMap } from 'api/journeys/requests';
import { useLists } from 'api/lists/hooks';

import { AttributeIcon } from '../common';

export type AttributeTemplateFormData = {
  title: string;
  attributeType: AttributeFieldType;
  input?: 'required' | 'optional' | 'disabled';
  options?: string;
  multipleFiles?: boolean;

  isDecimal?: boolean;
};

const Form = createForm<AttributeTemplateFormData>();

export const useAddAttributeSidebar = () =>
  // Since this sidebar is being used inside the external forms overlay (which has a z-index of 9991),
  // we need to set a higher z-index to ensure it appears above the overlay.
  useSidebar(AttributeSidebar, { variant: 'LARGE', zIndex: 9992 });

type AttributeData = XOR<{ attributeId: string }, { attribute: AttributeTemplateData }>;

type AttributeProps<DataType extends AttributeTemplateData | TravellerAttributeTemplateData> = XOR<
  { onCreate: (data: DataType) => Promise<any> },
  { onEdit: (data: DataType) => Promise<any> } & AttributeData
>;

type AttributeSidebarProps<IsTravellerAttribute extends boolean> = IsTravellerAttribute extends true
  ? {
      isTravellerAttribute: true;
    } & AttributeProps<TravellerAttributeTemplateData> & { shouldAnimate?: boolean }
  : {
      isTravellerAttribute: false;
    } & AttributeProps<TravellerAttributeTemplateData>;

const AttributeSidebar = <T extends boolean>(props: AttributeSidebarProps<T>) =>
  props.isTravellerAttribute ? (
    <TravellerAttributeFormContainer {...props} />
  ) : (
    <JourneyAttributeForm {...props} />
  );

const TravellerAttributeFormContainer = ({
  attributeId,
  onCreate,
  onEdit,
  shouldAnimate,
}: AttributeSidebarProps<true>) => {
  const { data, isLoading } = useCompanyTravellerAttributeTemplate(attributeId ?? '', true);
  if (attributeId && (isLoading || !data)) return <RelativeSpinner />;
  const attribute = data
    ? {
        attributeType: data.attributeType,
        title: data.templateTitle ?? '',
        options: data.options,
        isRequired: data.input === 'required',
        isActive: data.input === 'disabled' ? false : true,
        numTravellers: data.numTravellers,
        isSystemAttribute: data.isSystemAttribute,
      }
    : undefined;

  return (
    <TravellerAttributeForm
      attribute={attribute}
      onCreate={onCreate}
      onEdit={onEdit}
      shouldAnimate={shouldAnimate}
    />
  );
};

type TravellerAttributeFormProps = {
  attribute?: TravellerAttributeTemplateData;
  onCreate?: (data: TravellerAttributeTemplateData) => Promise<void>;
  onEdit?: (data: TravellerAttributeTemplateData) => Promise<void | undefined>;
  shouldAnimate?: boolean;
};

const TravellerAttributeForm = ({
  attribute,
  onCreate,
  onEdit,
  shouldAnimate,
}: TravellerAttributeFormProps) => {
  const { t } = useScopedTranslate(locale);
  const listsQuery = useLists();

  const showConfirmModal = useConfirmModal();
  const [chosenType, setChosenType] = useState<AttributeFieldType | undefined>(
    attribute?.attributeType ?? undefined
  );
  const [isAllowMultiple, setIsAllowMultiple] = useState(attribute?.attributeType === 'file_multi');
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const formValues = formStore.useFormValues();
  const formDecimal = formValues.isDecimal as boolean;

  const prepareData = (data: AttributeTemplateFormData) => {
    const inputStatus = data.input && visibilityMap[data.input] ? visibilityMap[data.input] : {};
    const isMultiFile = chosenType === 'file' && data.multipleFiles;
    const submitData = {
      ...data,
      ...(chosenType && { attributeType: isMultiFile ? 'file_multi' : chosenType }),
      ...inputStatus,
    };
    return submitData;
  };

  const handleSubmit = async (data: AttributeTemplateFormData) => {
    const submitData = prepareData(data);
    delete submitData.input;
    if (chosenType && onCreate) {
      await onCreate(submitData);
      hideSidebar();
    } else if (onEdit) {
      const numTravellers = attribute?.numTravellers ?? 0;
      if (numTravellers > 0) {
        showConfirmModal({
          title: t('updateAttribute'),
          description: t('updateAttributeDescription', {
            num_travellers: numTravellers,
            travellerNoun: numTravellers === 1 ? t('traveller') : t('travellers'),
          }),
          buttonText: t('updateAttribute'),
          buttonIcon: <IconReplace />,
          onConfirm: () => {
            onEdit(submitData);
            hideSidebar();
          },
        });
      } else {
        await onEdit(submitData);
        hideSidebar();
      }
    }
  };

  const inputOptions: SelectOption[] = [
    {
      value: 'required',
      label: <InputOptionLabel label={t('required')} color="elementGreen100" />,
    },
    {
      value: 'optional',
      label: <InputOptionLabel label={t('optional')} color="elementOrange100" />,
    },
    {
      value: 'disabled',
      label: <InputOptionLabel label={t('disabled')} color="elementPurple50" />,
    },
  ];

  const inputType = !attribute
    ? 'optional'
    : attribute?.isRequired
    ? 'required'
    : attribute?.isActive
    ? 'optional'
    : 'disabled';

  const isTypeChosen = Boolean(chosenType);
  const isEditing = Boolean(onEdit);
  const sidebarTitle = onCreate ? t('createTravellerAttribute') : t('editTravellerAttribute');

  return (
    <Sidebar>
      <AttributeHeader
        title={sidebarTitle}
        isEditing={isEditing}
        isTypeChosen={isTypeChosen}
        setChosenType={setChosenType}
        icon={<IconAttributeTraveller color="elementBlue100" />}
      />
      <Sidebar.Content>
        <AnimatePresence initial={shouldAnimate} exitBeforeEnter>
          {isTypeChosen ? (
            <Slide key="create-edit-attribute" offset={30}>
              <Stack space={6}>
                <AttributeTypeHeader attributeType={chosenType} />
                {attribute?.isSystemAttribute ? (
                  <>
                    <Stack space={4}>
                      <Stack space={0}>
                        <Text text={t('attributeLabel')} font="semibold14" />
                        <Text text={attribute?.title} font="regular14" />
                      </Stack>
                      <Stack space={0}>
                        <Text text={t('input')} font="semibold14" />
                        <Text text={t(inputType)} font="regular14" />
                      </Stack>
                    </Stack>
                    <InfoBox
                      icon={<IconFormIndicatorInfo color="indigo100" />}
                      text={t('canNotEditAttribute')}
                      font="regular14"
                      variant="indigo"
                    />
                    <Stack>
                      <Button
                        text={t('core.close')}
                        variant="secondary"
                        onClick={hideSidebar}
                        style={{ marginLeft: 'auto' }}
                      />
                    </Stack>
                  </>
                ) : (
                  <Form onSubmit={handleSubmit} store={formStore}>
                    <Stack space={4}>
                      <Form.TextField
                        id="title"
                        label={t('attributeLabel')}
                        validators={['required']}
                        defaultValue={attribute?.title}
                      />
                      <Form.Select
                        id="input"
                        label={t('input')}
                        validators={['required']}
                        defaultValue={inputType}
                        options={inputOptions}
                      />
                      {(chosenType === 'single_select' || chosenType === 'multi_select') && (
                        <Form.Select
                          id="options"
                          label={t('options')}
                          validators={['required']}
                          defaultValue={attribute?.options}
                          options={(listsQuery.data ?? []).map((list) => ({
                            value: list.shortId,
                            label: list.name,
                          }))}
                        />
                      )}
                      {(chosenType === 'file' || chosenType === 'file_multi') && (
                        <Stack orientation="horizontal" justify="space-between">
                          <Stack>
                            <Text font="semibold14" text={t('allowMultiple')} />
                            {isAllowMultiple ? (
                              <Text font="regular14" text={t('core.yes')} />
                            ) : (
                              <Text font="regular14" text={t('core.no')} color="textHelper" />
                            )}
                          </Stack>
                          {!isEditing && (
                            <Form.Switch
                              // @ts-ignore - We can't know the type of the value
                              id="multipleFiles"
                              label={t('allowMultiple')}
                              labelVisible={false}
                              onChange={setIsAllowMultiple}
                              defaultValue={
                                attribute?.attributeType === 'file_multi' ? true : false
                              }
                            />
                          )}
                        </Stack>
                      )}
                      {(chosenType === 'number' || chosenType === 'decimal') && (
                        <Stack space={6}>
                          <Stack space={4}>
                            <Stack
                              space={1}
                              orientation="horizontal"
                              align="center"
                              justify="space-between"
                            >
                              <Text font="regular14" text={t('allowDecimalNumbers')} />
                              <Form.Switch
                                // @ts-ignore
                                id="isDecimal"
                                label={t('allowDecimalNumbers')}
                                labelVisible={false}
                                defaultValue={attribute?.attributeType === 'decimal'}
                                onChange={(value) => {
                                  setChosenType(value ? 'decimal' : 'number');
                                }}
                                isDisabled={isEditing}
                              />
                            </Stack>
                          </Stack>
                          <NumberFieldExampleCard isDecimal={formDecimal ?? false} />
                        </Stack>
                      )}
                    </Stack>
                    <Sidebar.Footer>
                      <Form.Actions>
                        <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
                        <Form.Submit
                          text={isEditing ? t('core.save') : t('submit')}
                          variant={isEditing ? 'primary-blue' : undefined}
                          isDisabled={!isDirty}
                        />
                      </Form.Actions>
                    </Sidebar.Footer>
                  </Form>
                )}
              </Stack>
            </Slide>
          ) : (
            <AttributeSelector setChosenType={setChosenType} />
          )}
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

type JourneyAttributeFormProps = {
  attribute?: AttributeTemplateData;
  onCreate?: (data: AttributeTemplateData) => Promise<void>;
  onEdit?: (data: AttributeTemplateData) => Promise<void | undefined>;
  shouldAnimate?: boolean;
};

const JourneyAttributeForm = ({
  attribute,
  onCreate,
  onEdit,
  shouldAnimate,
}: JourneyAttributeFormProps) => {
  const { t } = useScopedTranslate(locale);
  const listsQuery = useLists();

  const [chosenType, setChosenType] = useState<AttributeFieldType | undefined>(
    attribute?.attributeType ?? undefined
  );
  const [isAllowMultiple, setIsAllowMultiple] = useState(attribute?.attributeType === 'file_multi');
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const formValues = formStore.useFormValues();
  const formDecimal = formValues.isDecimal as boolean;

  const prepareData = (data: AttributeTemplateFormData) => {
    const inputStatus = data.input && visibilityMap[data.input] ? visibilityMap[data.input] : {};
    const isMultiFile = chosenType === 'file' && data.multipleFiles;

    const submitData = {
      ...data,
      ...(chosenType && { attributeType: isMultiFile ? 'file_multi' : chosenType }),
      ...inputStatus,
    };
    return submitData;
  };

  const handleSubmit = async (data: AttributeTemplateFormData) => {
    const submitData = prepareData(data);
    delete submitData.input;
    delete submitData.multipleFiles;
    if (chosenType && onCreate) {
      await onCreate(submitData);
      hideSidebar();
    } else if (onEdit) {
      await onEdit(submitData);
      hideSidebar();
    }
  };

  const isTypeChosen = Boolean(chosenType);
  const sidebarTitle = onCreate ? t('addJourneyAttribute') : t('editJourneyAttribute');
  const isEditing = Boolean(onEdit);

  return (
    <Sidebar>
      <AttributeHeader
        title={sidebarTitle}
        isEditing={onEdit ? true : false}
        isTypeChosen={isTypeChosen}
        setChosenType={setChosenType}
        icon={<IconAttributeJourney color="elementBlue100" />}
      />
      <Sidebar.Content>
        <AnimatePresence initial={shouldAnimate} exitBeforeEnter>
          {isTypeChosen ? (
            <Slide key="create-edit-attribute" offset={30}>
              <Stack space={6}>
                <AttributeTypeHeader attributeType={chosenType} />
                <Form onSubmit={handleSubmit} store={formStore}>
                  <Form.TextField
                    id="title"
                    label={t('attributeLabel')}
                    validators={['required']}
                    defaultValue={attribute?.title}
                  />
                  {(chosenType === 'single_select' || chosenType === 'multi_select') && (
                    <Form.Select
                      id="options"
                      label={t('options')}
                      validators={['required']}
                      defaultValue={attribute?.options ?? undefined}
                      options={(listsQuery.data ?? []).map((list) => ({
                        value: list.shortId,
                        label: list.name,
                      }))}
                    />
                  )}
                  {(chosenType === 'file' || chosenType === 'file_multi') && (
                    <Stack orientation="horizontal" justify="space-between">
                      <Stack>
                        <Text font="semibold14" text={t('allowMultiple')} />
                        {isAllowMultiple ? (
                          <Text font="regular14" text={t('core.yes')} />
                        ) : (
                          <Text font="regular14" text={t('core.no')} color="textHelper" />
                        )}
                      </Stack>
                      {!isEditing && (
                        <Form.Switch
                          // @ts-ignore - We can't know the type of the value
                          id="multipleFiles"
                          label={t('allowMultiple')}
                          labelVisible={false}
                          onChange={setIsAllowMultiple}
                          defaultValue={attribute?.attributeType === 'file_multi' ? true : false}
                        />
                      )}
                    </Stack>
                  )}
                  {(chosenType === 'number' || chosenType === 'decimal') && (
                    <Stack space={6}>
                      <Stack space={4}>
                        <Stack
                          space={1}
                          orientation="horizontal"
                          align="center"
                          justify="space-between"
                        >
                          <Text font="regular14" text={t('allowDecimalNumbers')} />
                          <Form.Switch
                            // @ts-ignore
                            id="isDecimal"
                            label={t('allowDecimalNumbers')}
                            labelVisible={false}
                            defaultValue={attribute?.attributeType === 'decimal'}
                            onChange={(value) => {
                              setChosenType(value ? 'decimal' : 'number');
                            }}
                            isDisabled={isEditing}
                          />
                        </Stack>
                      </Stack>
                      <NumberFieldExampleCard isDecimal={formDecimal ?? false} />
                    </Stack>
                  )}
                  <Sidebar.Footer>
                    <Form.Actions>
                      <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
                      <Form.Submit
                        text={onEdit ? t('core.save') : t('submit')}
                        variant="primary-blue"
                        isDisabled={!isDirty}
                      />
                    </Form.Actions>
                  </Sidebar.Footer>
                </Form>
              </Stack>
            </Slide>
          ) : (
            <AttributeSelector setChosenType={setChosenType} />
          )}
        </AnimatePresence>
      </Sidebar.Content>
    </Sidebar>
  );
};

const InputOptionLabel = ({ label, color }: { label: string; color: Color }) => {
  return (
    <Stack orientation="horizontal" align="center" space={1}>
      <IconStatusIndicator color={color} />
      <Text text={label} font="regular14" />
    </Stack>
  );
};

const AttributeTypeHeader = ({ attributeType }: { attributeType?: AttributeFieldType }) => {
  const { t } = useScopedTranslate(locale);

  return (
    <div>
      <Stack space={2} orientation="horizontal" align="center">
        {attributeType && <AttributeIcon type={attributeType} color="elementPurple50" />}
        <Text text={t(attributeType ?? 'selectAttributeType')} font="bold18" />
      </Stack>
      <Text
        text={t(attributeType ? `${attributeType}Description` : 'selectAttributeTypeDescription')}
        font="regular14"
        color="textSecondary"
      />
    </div>
  );
};

const attributeTypeOptions: AttributeFieldType[] = [
  'text',
  'paragraph',
  'number',
  'single_select',
  'multi_select',
  'date',
  'phone_number',
  'email',
  'user',
  'file',
];

const AttributeSelector = ({
  setChosenType,
}: {
  setChosenType: (type: AttributeFieldType) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Slide key="attribute-type-list" offset={-30}>
      <Stack space={6}>
        <Stack space={6}>
          <AttributeTypeHeader />
          <Stack space={2}>
            {attributeTypeOptions.map((attributeType) => {
              const title = t(attributeType);

              return (
                <Card
                  key={title}
                  variant="transparent"
                  spaceX={4}
                  spaceY={2}
                  interactive
                  onClick={() => setChosenType(attributeType)}
                  style={{ cursor: 'pointer' }}
                >
                  <Stack space={4} key={title} orientation="horizontal" align="center">
                    <AttributeIcon type={attributeType} color="elementPurple50" />
                    <div>
                      <Text text={title} font="semibold14" />
                      <Text
                        text={t(`${attributeType}Description`)}
                        font="regular14"
                        color="textSecondary"
                      />
                    </div>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </Stack>
        <Stack align="end">
          <Button variant="secondary" onClick={hideSidebar} text={t('core.cancel')} />
        </Stack>
      </Stack>
    </Slide>
  );
};

const AttributeHeader = ({
  setChosenType,
  isTypeChosen,
  isEditing,
  icon,
  title,
}: {
  setChosenType: (type?: AttributeFieldType) => void;
  isTypeChosen: boolean;
  isEditing: boolean;
  icon: ReactElement<IconProps>;
  title: string;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <Sidebar.Header>
      <Stack space={4}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack space={2} orientation="horizontal" align="center">
            {isEditing ? <IconEdit color="elementBlue100" /> : <div>{icon}</div>}
            <Sidebar.HeaderTitle title={title} />
          </Stack>
          <Stack orientation="horizontal">
            <AnimatePresence initial={false}>
              {isTypeChosen && !isEditing && (
                <BasicFade>
                  <Button
                    variant="ghost"
                    text={t('core.back')}
                    iconLeft={<IconArrowLeft />}
                    onClick={() => setChosenType(undefined)}
                  />
                </BasicFade>
              )}
            </AnimatePresence>
            <CloseButton />
          </Stack>
        </Stack>
      </Stack>
    </Sidebar.Header>
  );
};

const locale = {
  en: {
    selectAttributeType: 'Select field type',
    selectAttributeTypeDescription: 'What type of field would you like to add?',
    date: 'Select - Date',
    dateDescription: 'For selecting dates',
    text: 'Text - Short',
    textDescription: 'Small general input field',
    paragraph: 'Text - Long',
    paragraphDescription: 'Large input field',
    user: 'Select - User',
    userDescription: 'For selecting users in the system',
    phone_number: 'Phone number',
    phone_numberDescription: 'For adding phone numbers',
    email: 'Email',
    emailDescription: 'For adding additional emails',
    single_select: 'Select - Single',
    single_selectDescription: 'For selecting a single option from a list',
    multi_select: 'Select - Multi',
    multi_selectDescription: 'For selecting multiple options from a list',
    boolean: 'On / off',
    booleanDescription: 'For selecting a boolean value',
    file: 'File upload',
    fileDescription: 'For file attachments',
    file_multi: 'File upload',
    file_multiDescription: 'For file attachments',
    number: 'Number',
    numberDescription: 'Small input field for numbers',
    decimal: 'Number',
    decimalDescription: 'Small input field for decimals',
    attributeLabel: 'Field label',
    attributeType: 'Field type',
    options: 'Options',
    submit: 'Create field',
    addJourneyAttribute: 'Add journey field',
    editJourneyAttribute: 'Edit journey field',
    createTravellerAttribute: 'Create traveller field',
    editTravellerAttribute: 'Edit traveller field',
    canNotEditAttribute: 'System fields can not be edited or removed',
    input: 'Input',
    required: 'Required',
    optional: 'Optional',
    disabled: 'Disabled',
    updateAttribute: 'Update field',
    updateAttributeDescription:
      'This update will affect {num_travellers} {travellerNoun}.\nAre you sure you want to update this field?',
    allowMultiple: 'Allow multiple files',
    traveller: 'traveller',
    travellers: 'travellers',
    allowDecimalNumbers: 'Allow decimal numbers',
  },
};
